import { useState } from "react";
import { apiClient } from "../../lib/api";
import { LayoutSection } from "../../lib/types";

interface LayoutSectionListProps {
  sections: LayoutSection[];
  onUpdate: () => void;
}

export function LayoutSectionList({
  sections,
  onUpdate,
}: LayoutSectionListProps) {
  const [editingSection, setEditingSection] = useState<LayoutSection | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const handleEdit = (section: LayoutSection) => {
    setEditingSection({
      ...section,
      allowed_aspect_ratios: JSON.stringify(section.allowed_aspect_ratios),
    });
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("このセクションを削除してもよろしいですか？")) {
      return;
    }

    try {
      await apiClient.delete(`/layout_sections/${id}`);
      onUpdate();
    } catch (err: any) {
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingSection) return;

    try {
      await apiClient.patch(`/layout_sections/${editingSection.id}`, {
        section_name: editingSection.section_name,
        description: editingSection.description,
        max_images: editingSection.max_images,
        allowed_aspect_ratios: JSON.parse(
          editingSection.allowed_aspect_ratios as string
        ),
      });

      setEditingSection(null);
      onUpdate();
    } catch (err: any) {
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}

      {/* ヘッダー */}
      <div className="grid grid-cols-[1fr_1fr_2fr_1fr_2fr_auto] gap-4 px-4 py-2 bg-gray-50 rounded-t-lg text-xs font-medium text-gray-500 uppercase">
        <div>ID</div>
        <div>セクション名</div>
        <div>説明</div>
        <div>最大画像数</div>
        <div>許可するアスペクト比</div>
        <div>操作</div>
      </div>

      {/* リスト */}
      <div className="space-y-2">
        {sections.map((section) =>
          editingSection?.id === section.id ? (
            <form
              key={section.id}
              onSubmit={handleUpdate}
              className="grid grid-cols-[1fr_1fr_2fr_1fr_2fr_auto] gap-4 px-4 py-3 bg-white rounded-lg items-center"
            >
              <div className="text-sm text-gray-500">{section.id}</div>
              <input
                type="text"
                value={editingSection.section_name}
                onChange={(e) =>
                  setEditingSection({
                    ...editingSection,
                    section_name: e.target.value,
                  })
                }
                className="text-sm p-1 border rounded"
                required
              />
              <input
                type="text"
                value={editingSection.description}
                onChange={(e) =>
                  setEditingSection({
                    ...editingSection,
                    description: e.target.value,
                  })
                }
                className="text-sm p-1 border rounded"
              />
              <input
                type="number"
                min={1}
                value={editingSection.max_images}
                onChange={(e) =>
                  setEditingSection({
                    ...editingSection,
                    max_images: parseInt(e.target.value),
                  })
                }
                className="text-sm p-1 border rounded"
                required
              />
              <input
                type="text"
                value={editingSection.allowed_aspect_ratios as string}
                onChange={(e) =>
                  setEditingSection({
                    ...editingSection,
                    allowed_aspect_ratios: e.target.value,
                  })
                }
                className="text-sm p-1 border rounded"
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-2 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700"
                >
                  保存
                </button>
                <button
                  type="button"
                  onClick={() => setEditingSection(null)}
                  className="px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                >
                  キャンセル
                </button>
              </div>
            </form>
          ) : (
            <div
              key={section.id}
              className="grid grid-cols-[1fr_1fr_2fr_1fr_2fr_auto] gap-4 px-4 py-3 bg-white rounded-lg hover:bg-gray-50 transition-colors duration-150 items-center"
            >
              <div className="text-sm text-gray-500">{section.id}</div>
              <div className="text-sm font-medium text-gray-900">
                {section.section_name}
              </div>
              <div className="text-sm text-gray-500 break-words">
                {section.description}
              </div>
              <div className="text-sm text-gray-500">{section.max_images}</div>
              <div className="text-sm text-gray-500 break-words">
                {JSON.stringify(section.allowed_aspect_ratios)}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(section)}
                  className="px-2 py-1 text-xs text-blue-600 bg-blue-100 rounded hover:bg-blue-200"
                >
                  編集
                </button>
                <button
                  onClick={() => handleDelete(section.id)}
                  className="px-2 py-1 text-xs text-red-600 bg-red-100 rounded hover:bg-red-200"
                >
                  削除
                </button>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}
